import {
  Comment,
  CommentCache,
  CommentStatus,
  ReportedComment,
} from '../../../domain/entities/Comment';
import { createNewEndpoint } from '../core/endpoint';
import { GraphQlMethods } from '../core/fetch';
import { AuthGuard } from '../core/guards/authGuard';
import { PreventUnauthenticatedGuard } from '../core/guards/preventUnauthenticated';
import { getDeal } from '../deals/endpoints';
import { CommentsActionType } from './actionTypes';
import { DELETE_COMMENT_MUTATION } from './mutations/deleteComment';
import { COMMENT_DISLIKE_MUTATION } from './mutations/dislikeComment';
import { EDIT_COMMENT_MUTATION } from './mutations/editComment';
import { LIKE_COMMENT_MUTATION } from './mutations/likeComment';
import { COMMENT_MUTATION } from './mutations/newComment';
import { REACT_COMMENT_MUTATION } from './mutations/reactComment';
import { REMOVE_COMMENT_REACT_MUTATION } from './mutations/removeCommentReact';
import { REPLY_MUTATION } from './mutations/replyComment';
import { REPORT_COMMENT_MUTATION } from './mutations/reportComment';
import { COMMENTS_QUERY, COMMENTS_QUERY_V2 } from './queries/comments';
import { PRIVATE_COMMENTS_QUERY } from './queries/privateComments';
import { TOP_COMMENTS_QUERY } from './queries/topComments';
import {
  CommentDislikeMutationVariables,
  CommentEditMutationVariables,
  CommentLikeDislike,
  CommentLikeMutationVariables,
  CommentMutationVariables,
  NewCommentResponse,
  CommentReplyCommentResponse,
  CommentsCache,
  CommentsDislikeResponse,
  CommentsEditResponse,
  CommentsLikeResponse,
  CommentsQuery,
  CommentsQueryResponse,
  CommentsQueryVariables,
  CommentsReportResponse,
  CommentsReportVariables,
  ReplyCommentMutationVariables,
  CommentsQueryV2Variables,
  CommentsQueryV2Response,
  CommentsQueryV2,
  CommentsCacheV2,
} from './types/CommentsQuery';
import {
  PrivateCommentsQueryResponse,
  PrivateCommentsQueryVariables,
} from './types/PrivateCommentsQuery';
import { ReactCommentParams, ReactCommentResponse } from './types/ReactComment';
import {
  RemoveCommentReactParams,
  RemoveCommentReactResponse,
} from './types/RemoveCommentReact';
import {
  TopCommentsQuery,
  TopCommentsQueryVariables,
} from './types/TopCommentsQuery';
import { CommentV2PageInfo, ReactCommentResult } from './types/shared';
import { dealIsSaved } from '../savedDeals/endpoints';
import { getUserPreference } from '../user/endpoints';
import { COMMENTS_REACTED_QUERY } from './queries/commentsReacted';
import { COMMENT_QUERY } from './queries/comment';
import {
  CommentQueryResponse,
  CommentQueryVariables,
} from './types/CommentQuery';
import { TEXT_SUGGESTIONS_QUERY } from './queries/textSuggestions';
import {
  CommentsSuggestionCache,
  TextSuggestion,
  TextSuggestionsQueryResponse,
  TextSuggestionsQueryVariables,
} from './types/CommentsSuggestionsQuery';
import {
  CommentsReactedResponse,
  CommentsReactedVariables,
  ReactionInfo,
} from './types/CommentsReactedQuery';
import { ResponseResult } from '../core/request';
import {
  getCommentsReactedProductInterceptor,
  reactProductCommentInterceptor,
  removeReactionProductCommentInterceptor,
  replyProductCommentInterceptor,
} from '../products/interceptors/productComments';

export function mapCommentsToReducersLists(edges: Comment[]) {
  const comments: Record<string, Comment> = edges.reduce(
    (acc, current) => ({ ...acc, [current.id]: current }),
    {}
  );

  const ids = edges.map(({ id }) => id);

  return { comments, ids };
}

export const getComments = createNewEndpoint<
  CommentsQueryResponse,
  CommentsQueryVariables,
  CommentsQuery,
  CommentsCache
>({
  id: CommentsActionType.GetComments,
  query: COMMENTS_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.commentsAndReplies,
  cacheTransform: (incoming, current) => {
    const { data, loading } = incoming;
    const { comments, ids } = mapCommentsToReducersLists(data?.edges || []);
    const currentPage = data?.pageInfo.page || 0;
    const totalComments = data?.pageInfo.total || 0;
    const totalPages = data?.pageInfo.totalPages || 0;
    const pages = { ...(current?.pages || {}), [currentPage]: ids } as Record<
      number,
      string[]
    >;

    if (current?.optmisticComment) {
      const alreadyHaveOptmisticCommentId = pages[currentPage].some(
        (id) => id === current.optmisticComment?.id
      );
      if (
        incoming.variables?.page === totalPages &&
        !alreadyHaveOptmisticCommentId
      ) {
        pages[currentPage]?.push(current.optmisticComment?.id);
      }
    }

    return {
      currentPage,
      totalComments,
      totalPages,
      loading,
      comments: { ...current?.comments, ...comments },
      pages,
    };
  },
});

export const getCommentsV2 = createNewEndpoint<
  CommentsQueryV2Response,
  CommentsQueryV2Variables,
  CommentsQueryV2,
  CommentsCacheV2
>({
  id: CommentsActionType.GetCommentsV2,
  query: COMMENTS_QUERY_V2,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.commentsAndRepliesV2,
  cacheTransform: (incoming, current) => {
    const { data, loading } = incoming;
    const { comments, ids } = mapCommentsToReducersLists(data?.edges || []);

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const newCommentCached = newComment.cache$
      .value as unknown as ResponseResult<Comment, CommentMutationVariables>;

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const currentCommentCached = getComment.cache$
      .value as unknown as ResponseResult<Comment, CommentMutationVariables>;

    let filteredIds = ids;
    if (newCommentCached) {
      if (ids?.includes(newCommentCached?.data?.id || '')) {
        filteredIds = ids?.filter((id) => id !== newCommentCached?.data?.id);
      }
    }

    if (currentCommentCached) {
      if (ids?.includes(currentCommentCached?.data?.id || '')) {
        filteredIds = ids?.filter(
          (id) => id !== currentCommentCached?.data?.id
        );
      }
    }

    const after = incoming?.variables?.after as string;

    let commentsReturn = { ...comments };
    let listIds = [...filteredIds];

    if (after) {
      commentsReturn = { ...current?.comments, ...comments };
      listIds = [...(current?.ids || []), ...filteredIds];
    }

    return {
      loading: after ? false : loading,
      isPaginationLoading: after ? loading : false,
      comments: commentsReturn,
      pageInfo: data?.pageInfo as CommentV2PageInfo,
      ids: listIds,
    };
  },
});

export const getTopComments = createNewEndpoint<
  TopCommentsQuery,
  TopCommentsQueryVariables,
  Comment[]
>({
  id: CommentsActionType.GetTopComments,
  query: TOP_COMMENTS_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.topCommentsAndReplies,
});

export const getPrivateComments = createNewEndpoint<
  PrivateCommentsQueryResponse,
  PrivateCommentsQueryVariables,
  Comment[],
  { privateComments?: Comment[] }
>({
  id: CommentsActionType.GetPrivateComments,
  query: PRIVATE_COMMENTS_QUERY,
  method: GraphQlMethods.Get,
  guards: [PreventUnauthenticatedGuard],
  transform: (response) => response.me.privateCommentsAndReplies,
  cacheTransform: ({ data }) => {
    const commentsCacheV2 = getCommentsV2.cache$.value;

    /* 
      Esse filtro é feito para evitar duplicação quando adicionamos 
      o resultado otimista para o usuáro ao criar novo comentário
    */
    const filteredData = data?.filter(
      (comment) => !commentsCacheV2?.ids?.includes(comment.id)
    );

    return {
      privateComments: filteredData,
    };
  },
});

export const likeComment = createNewEndpoint<
  CommentsLikeResponse,
  CommentLikeMutationVariables,
  CommentLikeDislike,
  void
>({
  id: CommentsActionType.PostlikeComment,
  query: LIKE_COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.likeComment,
  cacheTransform: ({ variables, loading }) => {
    if (!loading) return;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id].liked = true;
      current.comments[id].likeCount += 1;
    }
    getCommentsV2.cache$.next(current);
  },
});

export const dislikeComment = createNewEndpoint<
  CommentsDislikeResponse,
  CommentDislikeMutationVariables,
  CommentLikeDislike,
  void
>({
  id: CommentsActionType.PostDislikeComment,
  query: COMMENT_DISLIKE_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.dislikeComment,
  cacheTransform: ({ loading, variables }) => {
    if (!loading) return undefined;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id].liked = false;
      current.comments[id].likeCount -= 1;
    }

    getCommentsV2.cache$.next(current);
    return undefined;
  },
});

export const reportComment = createNewEndpoint<
  CommentsReportResponse,
  CommentsReportVariables,
  ReportedComment
>({
  id: CommentsActionType.PostReportComment,
  query: REPORT_COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.reportComment,
});

export const deleteComment = createNewEndpoint<
  boolean,
  { id: string },
  boolean,
  void
>({
  id: CommentsActionType.PostDeleteComment,
  query: DELETE_COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  cacheTransform: ({ variables, loading }) => {
    if (!loading) return;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id].status = CommentStatus.DELETED;
      current.comments[id].content = '';
    }
    const dealCache = getDeal.cache$.value;
    if (dealCache) dealCache.commentCount -= 1;

    getDeal.cache$.next(dealCache);

    getCommentsV2.cache$.next(current);
  },
});

export const editComment = createNewEndpoint<
  CommentsEditResponse,
  CommentEditMutationVariables,
  Comment,
  void
>({
  id: CommentsActionType.PostEditComment,
  query: EDIT_COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.editComment,
  cacheTransform: ({ loading, variables, data }) => {
    if (loading || !data) return;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id] = data;
      current.comments[id].status = CommentStatus.ACTIVE;
    }

    getCommentsV2.cache$.next(current);
  },
});

export const reactComment = createNewEndpoint<
  ReactCommentResponse,
  ReactCommentParams,
  ReactCommentResult,
  void
>({
  id: CommentsActionType.PostReactComment,
  query: REACT_COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  interceptors: [reactProductCommentInterceptor],
  transform: (response) => response.me.reactComment,
  cacheTransform: ({ loading, variables, data }) => {
    if (loading || !data) return;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id].reactions = data.reactions;
      current.comments[id].reacted = variables?.reaction;
      current.comments[id].totalReactionsCount += 1;
    }

    getCommentsV2.cache$.next(current);
  },
});

export const removeCommentReact = createNewEndpoint<
  RemoveCommentReactResponse,
  RemoveCommentReactParams,
  ReactCommentResult,
  void
>({
  id: CommentsActionType.PostRemoveCommentReact,
  query: REMOVE_COMMENT_REACT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  interceptors: [removeReactionProductCommentInterceptor],
  transform: (response) => response.me.removeCommentReact,
  cacheTransform: ({ loading, variables, data }) => {
    if (loading || !data) return;
    const current = getCommentsV2.cache$.value;
    const id = variables?.id || '';
    if (current?.comments[id]) {
      current.comments[id].reactions = data.reactions;
      current.comments[id].reacted = undefined;
      current.comments[id].totalReactionsCount -= 1;
    }

    getCommentsV2.cache$.next(current);
  },
});

export const newComment = createNewEndpoint<
  NewCommentResponse,
  CommentMutationVariables,
  Comment,
  CommentCache
>({
  id: CommentsActionType.PostNewComment,
  query: COMMENT_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.comment,
  cacheTransform: ({ data, loading }) => {
    if (loading || !data) return {};
    const dealCache = getDeal.cache$.value;
    const commentsCache = getComments.cache$.value;
    const commentsCacheV2 = getCommentsV2.cache$.value;
    const dealIsSavedCache = dealIsSaved.cache$.value;

    if (commentsCacheV2) {
      commentsCacheV2.optmisticComment = data;
      commentsCacheV2.ids?.unshift(data.id);
      commentsCacheV2.comments = {
        [data.id]: { ...data, status: CommentStatus.ACTIVE },
        ...commentsCacheV2.comments,
      };
    }

    if (commentsCache) {
      commentsCache.optmisticComment = data;
      commentsCache.totalComments += 1;
      commentsCache.comments = {
        ...commentsCache.comments,
        [data.id]: { ...data, status: CommentStatus.ACTIVE },
      };

      const hasPagination = commentsCache?.totalPages > 1;
      const isLastPage = commentsCache.currentPage === commentsCache.totalPages;

      if (hasPagination && !isLastPage) {
        getComments.requestAsPromise({
          offerId: dealCache?.id || '',
          anchorCommentId: data.id,
          page: commentsCache.totalPages,
        });
      } else {
        commentsCache.totalPages = 1;
        commentsCache.pages[commentsCache.currentPage].push(data.id);
      }
    }

    if (dealCache) {
      dealCache.commentCount += 1;
    }

    getUserPreference.requestAsPromise().then((result) => {
      if (result.data?.followOffer.followCommentedOffer && dealCache) {
        const newDealIsSavedCache = {
          ...dealIsSavedCache,
          [dealCache.id]: {
            id: dealCache.id,
            notificationsEnabled: true,
            offer: dealCache,
          },
        };

        dealIsSaved.cache$.next(newDealIsSavedCache);
      }
    });

    getDeal.cache$.next(dealCache);
    getComments.cache$.next(commentsCache);
    getCommentsV2.cache$.next(commentsCacheV2);

    return { commentId: data.id };
  },
});

export const replyComment = createNewEndpoint<
  CommentReplyCommentResponse,
  ReplyCommentMutationVariables,
  Comment,
  void
>({
  id: CommentsActionType.PostReplyComment,
  query: REPLY_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  interceptors: [replyProductCommentInterceptor],
  transform: (response) => response.me.replyAsComment,
  cacheTransform: ({ data, loading }) => {
    if (loading || !data) return;
    const dealCache = getDeal.cache$.value;
    const commentsCache = getComments.cache$.value;

    const commentsCacheV2 = getCommentsV2.cache$.value;

    if (commentsCacheV2) {
      commentsCacheV2.optmisticComment = data;
      commentsCacheV2.ids?.unshift(data.id);
      commentsCacheV2.comments = {
        [data.id]: { ...data, status: CommentStatus.ACTIVE },
        ...commentsCacheV2.comments,
      };
    }

    if (commentsCache) {
      commentsCache.optmisticComment = data;
      commentsCache.totalComments += 1;
      commentsCache.comments = {
        ...commentsCache.comments,
        [data.id]: { ...data, status: CommentStatus.ACTIVE },
      };

      const hasPagination = commentsCache?.totalPages > 1;
      const isLastPage = commentsCache.currentPage === commentsCache.totalPages;

      if (hasPagination && !isLastPage) {
        getComments.requestAsPromise({
          offerId: dealCache?.id || '',
          anchorCommentId: data.id,
          page: commentsCache.totalPages,
        });
      } else {
        commentsCache.totalPages = 1;
        commentsCache.pages[commentsCache.currentPage].push(data.id);
      }
    }

    if (dealCache) dealCache.commentCount += 1;

    getDeal.cache$.next(dealCache);
    getComments.cache$.next(commentsCache);
    getCommentsV2.cache$.next(commentsCacheV2);
    newComment.cache$.next({ commentId: data.id });
  },
});

export const getCommentsReacted = createNewEndpoint<
  CommentsReactedResponse,
  CommentsReactedVariables,
  ReactionInfo[],
  void
>({
  id: CommentsActionType.GetCommentsReacted,
  query: COMMENTS_REACTED_QUERY,
  method: GraphQlMethods.Get,
  guards: [AuthGuard],
  interceptors: [getCommentsReactedProductInterceptor],
  transform: (response) => response.me.commentsReacted,
  cacheTransform: ({ data, loading }) => {
    if (loading || !data) return;
    const commentsCache = getComments.cache$.value;

    const commentsCacheV2 = getCommentsV2.cache$.value;

    data.map((reactionInfo) => {
      const id = reactionInfo.commentId || '';
      if (commentsCacheV2?.comments[id])
        commentsCacheV2.comments[id].reacted = reactionInfo.reacted;
      if (commentsCache?.comments[id])
        commentsCache.comments[id].reacted = reactionInfo.reacted;

      return reactionInfo;
    });

    getCommentsV2.cache$.next(commentsCacheV2);
    getComments.cache$.next(commentsCache);
  },
});
export const getComment = createNewEndpoint<
  CommentQueryResponse,
  CommentQueryVariables,
  Comment,
  void
>({
  id: CommentsActionType.GetComment,
  query: COMMENT_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.comment,
  cacheTransform: ({ data, loading }) => {
    if (loading || !data) return;

    const commentsCacheV2 = getCommentsV2.cache$.value;
    if (commentsCacheV2 && data.status === CommentStatus.ACTIVE) {
      commentsCacheV2.optmisticComment = data;
      commentsCacheV2.ids?.unshift(data.id);
      commentsCacheV2.comments = {
        [data.id]: { ...data },
        ...commentsCacheV2.comments,
      };
    }

    getCommentsV2.cache$.next(commentsCacheV2);
  },
});

export const getCommentsSuggestions = createNewEndpoint<
  TextSuggestionsQueryResponse,
  TextSuggestionsQueryVariables,
  TextSuggestion[],
  CommentsSuggestionCache
>({
  id: CommentsActionType.GetCommentsSuggestions,
  query: TEXT_SUGGESTIONS_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.textSuggestions,
  cacheTransform: ({ data }) => ({ commentSuggestions: data }),
});
